import { useEffect, useState } from "react";

import { Button } from "../components/Button";
import { joinGroup, requestMagicLink, type Me } from "../api";

/**
 * Invite-link landing page.
 *
 * URL shape: /join/<CODE>  (e.g. /join/MAPLE-7K2P)
 *
 * If the user is already signed in: auto-joins them, then drops them into
 * the chat. If not: collects an email, sends a magic link, and the
 * verify-token handler on the agent will sign them in and bring them back
 * here — at which point this component finds the cookie and completes the
 * join.
 */
export function Join({
  code,
  me,
  onJoined,
}: {
  code: string;
  me: Me | null;
  onJoined: () => void;
}) {
  const [phase, setPhase] = useState<"joining" | "joined" | "need-signin" | "error">(
    me ? "joining" : "need-signin",
  );
  const [groupName, setGroupName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    joinGroup(code)
      .then((res) => {
        if (cancelled) return;
        setGroupName(res.name);
        setPhase("joined");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "join failed");
        setPhase("error");
      });
    return () => { cancelled = true; };
  }, [me, code]);

  useEffect(() => {
    if (phase !== "joined") return;
    const t = setTimeout(() => {
      window.history.replaceState(null, "", "/");
      onJoined();
    }, 1400);
    return () => clearTimeout(t);
  }, [phase, onJoined]);

  return (
    <div className="grid min-h-full place-items-center px-6 py-16">
      <div className="w-full max-w-md">
        <a href="https://clubbot.ai" className="mb-10 flex items-center justify-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-ink flex items-center justify-center">
            <div className="w-4 h-4 rounded-full bg-accent" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">ClubBot</span>
        </a>

        <div className="rounded-md border-2 border-ink/15 bg-bg-2 p-8">
          {phase === "joining" && <JoiningState />}
          {phase === "joined" && (
            <JoinedState groupName={groupName} onContinue={onJoined} />
          )}
          {phase === "need-signin" && <NeedSigninState code={code} />}
          {phase === "error" && <ErrorState error={error} />}
        </div>
      </div>
    </div>
  );
}

function Chip({
  children,
  variant = "accent",
}: {
  children: React.ReactNode;
  variant?: "accent" | "danger";
}) {
  const cls =
    variant === "danger"
      ? "bg-[#C84B31] text-bg"
      : "bg-accent text-ink";
  return (
    <span className={
      "inline-block px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em] font-bold " + cls
    }>
      {children}
    </span>
  );
}

function JoiningState() {
  return (
    <div className="text-center">
      <Chip>Joining…</Chip>
      <h1 className="mt-3 font-display text-2xl font-bold tracking-tight">
        Hold on a second.
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed text-ink/65">
        Adding you to the club.
      </p>
    </div>
  );
}

function JoinedState({
  groupName,
  onContinue,
}: {
  groupName: string | null;
  onContinue: () => void;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span className="relative inline-flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
        </span>
        <Chip>You're in</Chip>
      </div>
      <h1 className="font-display text-3xl font-bold leading-tight tracking-tight">
        Welcome to{" "}
        {groupName ? (
          <span className="bg-accent text-ink px-1">{groupName}</span>
        ) : (
          "the club"
        )}.
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed text-ink/65">
        We're handing you off to the chat in a sec — or click below to jump in.
      </p>
      <div className="mt-6">
        <Button onClick={onContinue} className="w-full">
          Open chat →
        </Button>
      </div>
    </div>
  );
}

function NeedSigninState({ code }: { code: string }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setError(null);
    try {
      await requestMagicLink(email, `/join/${code}`);
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "something went wrong");
    }
  }

  if (status === "sent") {
    return (
      <div>
        <Chip>Magic link sent</Chip>
        <h1 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight">
          Check your email.
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-ink/65">
          We sent a sign-in link to{" "}
          <span className="font-mono text-sm bg-ink/5 px-1.5 py-0.5">{email}</span>.
          Click it and we'll finish joining you to the club.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-7">
        <Chip>Invite to a ClubBot club</Chip>
        <h1 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight">
          One step to join.
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-ink/65">
          Enter your email — we'll send a one-time link, and you'll be in the
          group right after.
        </p>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <input
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full"
          disabled={status === "sending"}
          autoFocus
        />
        <Button type="submit" disabled={status === "sending"} className="w-full">
          {status === "sending" ? "Sending…" : "Email me a sign-in link"}
        </Button>
        {error && (
          <p className="pt-1 font-mono text-xs tracking-wide text-[#C84B31]">× {error}</p>
        )}
      </form>
      <p className="mt-6 font-mono text-[11px] tracking-wider text-ink/45">
        INVITE CODE: <span className="text-ink/70">{code}</span>
      </p>
    </div>
  );
}

function ErrorState({ error }: { error: string | null }) {
  return (
    <div>
      <Chip variant="danger">Couldn't join</Chip>
      <h1 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight">
        That invite didn't work.
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed text-ink/65">
        {error ?? "The link may be invalid or expired."}
      </p>
      <p className="mt-6 font-mono text-[11px] tracking-wider text-ink/45">
        Ask the club admin for a fresh invite link.
      </p>
    </div>
  );
}
